import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

// Loop's bundle builder (/a/loop_subscriptions/bundle/...) adds to cart from its own
// script and fires no cart event, so we watch its add-to-cart button instead.
const LOOP_BUNDLE_ADD_TO_CART_SELECTOR = '.loop-btn-add-to-cart';
const CART_POLL_INTERVAL_MS = 500;
const CART_POLL_TIMEOUT_MS = 10000;

let loopPollId = 0;

/**
 * After the Loop bundle builder's add-to-cart button is clicked, poll the cart until its
 * count changes (the app exposes no completion hook), then dispatch the standard cart
 * update event so the bubble, drawer and cart items all refresh like a native add.
 * @param {MouseEvent} event
 */
async function onLoopBundleAddToCart(event) {
  if (!(event.target instanceof Element)) return;

  const button = event.target.closest(LOOP_BUNDLE_ADD_TO_CART_SELECTOR);
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;

  const pollId = ++loopPollId;
  const cartUrl = `${Theme.routes.cart_url}.js`;
  const fetchCart = async () => {
    const response = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.json();
  };

  // Baseline from the bubble rather than a fetch, which could race Loop's own add request.
  const cartIcon = /** @type {CartIcon | null} */ (document.querySelector('cart-icon'));
  const startCount = cartIcon?.currentCartCount || 0;
  const deadline = Date.now() + CART_POLL_TIMEOUT_MS;

  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, CART_POLL_INTERVAL_MS));
    // A newer click took over the polling.
    if (pollId !== loopPollId) return;

    try {
      const cart = await fetchCart();
      if (cart.item_count === startCount) continue;

      const deferredPromise = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [],
          promise: deferredPromise.promise,
          detail: { source: 'loop-bundle' },
        })
      );
      // No `sections` in the detail, so cart-items re-renders itself from the server.
      deferredPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          source: 'loop-bundle',
          didError: false,
        },
      });
      return;
    } catch (_) {
      // Network hiccup: try again on the next tick.
    }
  }
}

// Registered once per page (not per <cart-icon>) so a click dispatches a single update.
// Capture phase so it still runs if the app stops the click from bubbling.
document.addEventListener('click', onLoopBundleAddToCart, true);

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartLinesUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const itemCount = cart?.totalQuantity ?? detail?.itemCount ?? 0;

        this.renderCartBubble(itemCount);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] Event promise rejected:', error);
      });
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The absolute number of items in the cart.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
